import React from "react";
import {
  Avatar,
  Box,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import { reviews } from "../../constants/rewies";

const StarRating = ({ count = 5 }) => (
  <Box display="flex" gap={0.3} mb={2}>
    {Array.from({ length: count }).map((_, i) => (
      <StarIcon key={i} sx={{ fontSize: 18, color: "#F59E0B" }} />
    ))}
  </Box>
);

const ReviewCard = ({ user }) => (
  <Box
    sx={{
      p: 4,
      borderRadius: "24px",
      border: "1px solid rgba(13,27,75,0.07)",
      bgcolor: "#fff",
      boxShadow: "0 4px 20px rgba(13,27,75,0.06)",
      width: "100%",
      flex: 1,           // fills the flex-column Grid item — no grey bleed
      display: "flex",
      flexDirection: "column",
      position: "relative",
      overflow: "hidden",
      transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
      cursor: "default",
      "&:hover": {
        transform: "translateY(-8px)",
        boxShadow: "0 24px 60px rgba(13,27,75,0.12)",
        borderColor: "rgba(79,110,247,0.2)",
        "& .quote-icon": { opacity: 1, transform: "scale(1)" },
      },
    }}
  >
    {/* Quote decoration */}
    <Box
      className="quote-icon"
      sx={{
        position: "absolute",
        top: 20,
        right: 20,
        opacity: 0.06,
        transform: "scale(0.9)",
        transition: "all 0.3s ease",
      }}
    >
      <FormatQuoteIcon sx={{ fontSize: 56, color: "#4F6EF7" }} />
    </Box>

    {/* Stars */}
    <StarRating />

    {/* Review text */}
    <Typography
      variant="body2"
      color="#475569"
      lineHeight={1.8}
      sx={{ flex: 1, mb: 3, fontSize: "0.92rem" }}
    >
      "{user.review}"
    </Typography>

    {/* Divider */}
    <Box sx={{ height: "1px", bgcolor: "rgba(13,27,75,0.08)", mb: 3, flexShrink: 0 }} />

    {/* Author */}
    <Box display="flex" alignItems="center" gap={2}>
      <Avatar
        alt={user.name}
        src={user.imageUrl}
        sx={{
          width: 48,
          height: 48,
          border: "2px solid rgba(79,110,247,0.2)",
        }}
      />
      <Box>
        <Typography fontWeight={700} fontSize="0.9rem" color="#0D1B4B">
          {user.name}
        </Typography>
        <Typography fontSize="0.78rem" color="#64748B">
          {user.designation}
          {user.company ? ` · ${user.company}` : ""}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const ReviewCardSection = () => {
  return (
    <Box
      id="Reviews"
      sx={{
        py: { xs: 10, md: 14 },
        bgcolor: "#fff",
        position: "relative",
        overflow: "hidden", // keeps Grid's negative-margin from exposing page body background
      }}
    >
      {/* Subtle background circle — pointer-events none, no colour bleed */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          width: 900,
          height: 900,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(79,110,247,0.04) 0%, transparent 65%)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <Box textAlign="center" mb={{ xs: 7, md: 9 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 0.8,
              borderRadius: "100px",
              bgcolor: "rgba(79,110,247,0.08)",
              border: "1px solid rgba(79,110,247,0.2)",
              mb: 3,
            }}
          >
            <Typography fontSize="0.82rem" fontWeight={600} color="#4F6EF7" letterSpacing="0.04em">
              TESTIMONIALS
            </Typography>
          </Box>

          <Typography
            variant="h2"
            fontWeight={900}
            color="#0D1B4B"
            gutterBottom
            sx={{ fontSize: { xs: "1.8rem", md: "2.8rem" }, mb: 2 }}
          >
            Join Thousands of Satisfied Customers
          </Typography>

          <Typography
            variant="body1"
            color="#64748B"
            sx={{ maxWidth: 580, mx: "auto", lineHeight: 1.8 }}
          >
            Don't just take our word for it. Hear what our customers have to say
            about their experience with MySchoolling.
          </Typography>

          {/* Trust stat */}
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={0.5}
            mt={2.5}
          >
            {[1, 2, 3, 4, 5].map((i) => (
              <StarIcon key={i} sx={{ fontSize: 22, color: "#F59E0B" }} />
            ))}
            <Typography fontWeight={700} color="#0D1B4B" ml={1} fontSize="0.95rem">
              4.9 / 5 average rating
            </Typography>
          </Box>
        </Box>

        {/* Reviews grid */}
        <Grid container spacing={3} justifyContent="center" alignItems="stretch">
          {reviews.map((user) => (
            <Grid
              key={user.id}
              item
              xs={12}
              sm={6}
              md={3}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <ReviewCard user={user} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ReviewCardSection;
